import React, { useState, useEffect } from "react";
import {
	Box,
	Typography,
	Button,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	IconButton,
	Menu,
	MenuItem,
	CircularProgress,
	InputAdornment,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import supabase from "../../../supabaseClient";
import { useNavigate } from "react-router-dom";

export default function AdminInvoices() {
	const [invoices, setInvoices] = useState([]);
	const [loading, setLoading] = useState(true);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [form, setForm] = useState({
		customer_name: "",
		customer_email: "",
		phone_number: "",
		//new date + 30 days in YYYY-MM-DD format
		due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
			.toISOString()
			.slice(0, 10),
		line_items: [{ description: "", quantity: 1, price: 0 }],
		total: 0,
	});
	const [saveStatus, setSaveStatus] = useState("");

	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [editForm, setEditForm] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedInvoice, setSelectedInvoice] = useState(null);
	const [selectedDeleteInvoice, setSelectedDeleteInvoice] = useState(null);
	const navigate = useNavigate();

	const [markAsPaidAnchorEl, setMarkAsPaidAnchorEl] = useState(null);
	const [markAsPaidForm, setMarkAsPaidForm] = useState({
		paymentMethod: "",
		paymentNotes: "",
		paymentDate: new Date().toISOString().slice(0, 10),
		paymentAmount: 0,
	});

	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

	useEffect(() => {
		fetchInvoices();
	}, []);

	const fetchInvoices = async () => {
		setLoading(true);
		const { data, error } = await supabase
			.from("invoices")
			.select("*")
			.order("due_date", { ascending: true });
		if (error) {
			console.error("Error fetching invoices:", error);
		} else {
			setInvoices(data || []);
		}
		setLoading(false);
	};

	const handleOpen = () => {
		setDialogOpen(true);
	};

	const handleClose = () => {
		setDialogOpen(false);
		setForm({
			customer_name: "",
			customer_email: "",
			phone_number: "",
			due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
			line_items: [{ description: "", quantity: 1, price: 0 }],
			total: 0,
		});
		setSaveStatus("");
	};

	const handleChange = (e) => {
		setForm({ ...form, [e.target.name]: e.target.value });
	};

	const handleLineItemChange = (index, field, value) => {
		const updatedLineItems = [...form.line_items];
		updatedLineItems[index][field] = value;
		setForm({
			...form,
			line_items: updatedLineItems,
			total: updatedLineItems.reduce(
				(sum, item) => sum + item.quantity * item.price,
				0,
			),
		});
	};

	const addLineItem = () => {
		setForm({
			...form,
			line_items: [
				...form.line_items,
				{ description: "", quantity: 1, price: 0 },
			],
		});
	};

	const removeLineItem = (index) => {
		const updatedLineItems = form.line_items.filter((_, i) => i !== index);
		setForm({
			...form,
			line_items: updatedLineItems,
			total: updatedLineItems.reduce(
				(sum, item) => sum + item.quantity * item.price,
				0,
			),
		});
	};

	const handleSave = async () => {
		setSaveStatus("");
		setLoading(true);
		try {
			const { error } = await supabase
				.from("invoices")
				.insert([form])
				.select("*")
				.single();

			if (error) {
				console.error("Error saving invoice:", error);
				setSaveStatus("error");
				setLoading(false);
				return;
			}

			setSaveStatus("success");
			await fetchInvoices();
			handleClose();
		} catch (saveError) {
			console.error("Error saving invoice:", saveError);
			setSaveStatus("error");
		}
		setLoading(false);
	};

	const handleMenuOpen = (event, invoice) => {
		setAnchorEl(event.currentTarget);
		setSelectedInvoice(invoice);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
		setSelectedInvoice(null);
	};

	const handleViewInvoice = () => {
		if (selectedInvoice) {
			navigate(`/invoice/${selectedInvoice.id}`);
			handleMenuClose();
		}
	};

	const handleEditOpen = () => {
		setEditForm(selectedInvoice);
		setEditDialogOpen(true);
		handleMenuClose();
	};

	const handleEditClose = () => {
		setEditDialogOpen(false);
		setEditForm(null);
	};

	const handleEditChange = (e) => {
		setEditForm({ ...editForm, [e.target.name]: e.target.value });
	};

	const handleEditLineItemChange = (index, field, value) => {
		const updatedLineItems = [...editForm.line_items];
		updatedLineItems[index][field] = value;
		setEditForm({
			...editForm,
			line_items: updatedLineItems,
			total: updatedLineItems.reduce(
				(sum, item) => sum + item.quantity * item.price,
				0,
			),
		});
	};

	const addEditLineItem = () => {
		setEditForm({
			...editForm,
			line_items: [
				...editForm.line_items,
				{ description: "", quantity: 1, price: 0 },
			],
		});
	};

	const removeEditLineItem = (index) => {
		const updatedLineItems = editForm.line_items.filter((_, i) => i !== index);
		setEditForm({
			...editForm,
			line_items: updatedLineItems,
			total: updatedLineItems.reduce(
				(sum, item) => sum + item.quantity * item.price,
				0,
			),
		});
	};

	const handleEditSave = async () => {
		try {
			const { error } = await supabase
				.from("invoices")
				.update(editForm)
				.eq("id", editForm.id);

			if (error) {
				console.error("Error updating invoice:", error);
			} else {
				await fetchInvoices();
				handleEditClose();
			}
		} catch (err) {
			console.error("Error saving invoice edits:", err);
		}
	};

	const handleMarkAsPaidOpen = (event, invoice) => {
		setSelectedInvoice(invoice);
		setMarkAsPaidForm({
			paymentMethod: "",
			paymentNotes: "",
			paymentAmount: invoice.total,
			paymentDate: new Date().toISOString().slice(0, 10),
		});
		setMarkAsPaidAnchorEl(event.currentTarget);
	};

	const handleMarkAsPaidClose = () => {
		setMarkAsPaidAnchorEl(null);
		setMarkAsPaidForm({
			paymentMethod: "",
			paymentNotes: "",
			paymentAmount: 0,
			paymentDate: new Date().toISOString().slice(0, 10),
		});
		handleMenuClose();
	};

	const handleMarkAsPaidChange = (e) => {
		setMarkAsPaidForm({ ...markAsPaidForm, [e.target.name]: e.target.value });
	};

	const handleMarkAsPaidSubmit = async () => {
		if (!selectedInvoice) return;

		try {
			const { error: paymentError } = await supabase.from("payments").insert([
				{
					invoice_id: selectedInvoice.id,
					amount: markAsPaidForm.paymentAmount,
					payment_method: markAsPaidForm.paymentMethod,
					payment_date: markAsPaidForm.paymentDate,
					note: markAsPaidForm.paymentNotes,
				},
			]);

			if (paymentError) {
				console.error("Error recording payment:", paymentError);
				return;
			}

			const { error } = await supabase
				.from("invoices")
				.update({
					status: "Paid",
				})
				.eq("id", selectedInvoice.id);

			if (error) {
				console.error("Error marking invoice as paid:", error);
			} else {
				await fetchInvoices();
				handleMarkAsPaidClose();
			}
		} catch (err) {
			console.error("Error updating invoice status:", err);
		}
	};

	const handleDeleteOpen = (invoice) => {
		setDeleteDialogOpen(true);
		setSelectedDeleteInvoice(invoice);
		handleMenuClose();
	};

	const handleDeleteClose = () => {
		setDeleteDialogOpen(false);
		setSelectedDeleteInvoice(null);
	};

	const handleDeleteConfirm = async () => {
		if (!selectedDeleteInvoice) {
			console.log("No invoice selected for deletion.");
			return;
		}

		try {
			const { error } = await supabase
				.from("invoices")
				.delete()
				.eq("id", selectedDeleteInvoice.id);

			if (error) {
				console.error("Error deleting invoice:", error);
			} else {
				await fetchInvoices();
				handleDeleteClose();
			}
		} catch (err) {
			console.error("Error deleting invoice:", err);
		}
	};

	return (
		<Box sx={{ mx: "auto", width: "100%" }}>
			<Typography variant="h5" gutterBottom>
				Manage Invoices
			</Typography>
			<Button
				variant="contained"
				color="primary"
				onClick={handleOpen}
				sx={{ mb: 2 }}
			>
				Create New Invoice
			</Button>
			{loading ? (
				<CircularProgress />
			) : (
				<Box sx={{ width: "100%", overflowX: "auto" }}>
					<Table size="small" sx={{ minWidth: 500, width: "100%" }}>
						<TableHead>
							<TableRow>
								<TableCell>Invoice #</TableCell>
								<TableCell>Customer Name</TableCell>
								<TableCell>Due Date</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Total</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{invoices.map((invoice) => (
								<TableRow key={invoice.id}>
									<TableCell>{invoice.invoice_number || "-"}</TableCell>
									<TableCell>{invoice.customer_name}</TableCell>
									<TableCell>{invoice.due_date?.slice(0, 10)}</TableCell>
									<TableCell>{invoice.status}</TableCell>
									<TableCell>${invoice.total.toFixed(2)}</TableCell>
									<TableCell>
										<IconButton
											onClick={(event) => handleMenuOpen(event, invoice)}
										>
											<MoreVertIcon />
										</IconButton>
										{selectedInvoice?.id === invoice.id && (
											<Menu
												anchorEl={anchorEl}
												open={Boolean(anchorEl)}
												onClose={handleMenuClose}
											>
												<MenuItem onClick={handleEditOpen}>Edit</MenuItem>
												<MenuItem onClick={handleViewInvoice}>View</MenuItem>
												{invoice.status !== "Paid" && (
													<MenuItem
														onClick={(event) =>
															handleMarkAsPaidOpen(event, invoice)
														}
													>
														Mark as Paid
													</MenuItem>
												)}
												<MenuItem onClick={() => handleDeleteOpen(invoice)}>
													Delete
												</MenuItem>
											</Menu>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			)}
			<Dialog
				open={dialogOpen}
				onClose={handleClose}
				fullWidth
				maxWidth="sm"
				sx={{ "& .MuiDialog-paper": { width: "100%" } }}
			>
				<DialogTitle>Create New Invoice</DialogTitle>
				<DialogContent sx={{ p: 1 }}>
					<TextField
						label="Customer Name"
						name="customer_name"
						value={form.customer_name}
						onChange={handleChange}
						fullWidth
						margin="normal"
						size="small"
					/>
					<TextField
						label="Customer Email"
						name="customer_email"
						value={form.customer_email}
						onChange={handleChange}
						fullWidth
						margin="normal"
						size="small"
					/>
					<TextField
						label="Phone Number"
						name="phone_number"
						value={form.phone_number}
						onChange={handleChange}
						fullWidth
						margin="normal"
						size="small"
					/>
					<TextField
						label="Due Date"
						name="due_date"
						value={
							form.due_date
								? new Date(form.due_date).toISOString().slice(0, 10)
								: ""
						}
						onChange={handleChange}
						type="date"
						fullWidth
						margin="normal"
						InputLabelProps={{ shrink: true }}
						size="small"
					/>
					<Typography variant="subtitle1" sx={{ mt: 2 }}>
						Line Items
					</Typography>
					{form.line_items.map((item, index) => (
						<Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
							<TextField
								label="Description"
								value={item.description}
								onChange={(e) =>
									handleLineItemChange(index, "description", e.target.value)
								}
								fullWidth
								size="small"
							/>
							<TextField
								label="Quantity"
								type="number"
								value={item.quantity}
								onChange={(e) =>
									handleLineItemChange(
										index,
										"quantity",
										Number(e.target.value),
									)
								}
								size="small"
								sx={{ width: 100 }}
							/>
							<TextField
								label="Price"
								type="number"
								value={item.price}
								onChange={(e) =>
									handleLineItemChange(index, "price", Number(e.target.value))
								}
								size="small"
								sx={{ width: 100 }}
							/>
							<Button
								size="small"
								color="error"
								onClick={() => removeLineItem(index)}
							>
								Remove
							</Button>
						</Box>
					))}
					<Button size="small" onClick={addLineItem} sx={{ mt: 1 }}>
						Add Line Item
					</Button>
					<Typography variant="subtitle1" sx={{ mt: 2 }}>
						Total: ${form.total.toFixed(2)}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} disabled={loading}>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						variant="contained"
						color="primary"
						disabled={loading}
					>
						{loading ? <CircularProgress size={20} /> : "Save"}
					</Button>
				</DialogActions>
				{saveStatus === "success" && (
					<Box p={2} color="success.main">
						Invoice saved successfully.
					</Box>
				)}
				{saveStatus === "error" && (
					<Box p={2} color="error.main">
						Error saving invoice. Please try again.
					</Box>
				)}
			</Dialog>

			{/* Edit Invoice Dialog */}
			<Dialog
				open={editDialogOpen}
				onClose={handleEditClose}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle>Edit Invoice</DialogTitle>
				<DialogContent>
					<TextField
						label="Customer Name"
						name="customer_name"
						value={editForm?.customer_name || ""}
						onChange={handleEditChange}
						fullWidth
						margin="normal"
					/>
					<TextField
						label="Customer Email"
						name="customer_email"
						value={editForm?.customer_email || ""}
						onChange={handleEditChange}
						fullWidth
						margin="normal"
					/>
					<TextField
						label="Phone Number"
						name="phone_number"
						value={editForm?.phone_number || ""}
						onChange={handleEditChange}
						fullWidth
						margin="normal"
					/>
					<TextField
						label="Due Date"
						name="due_date"
						type="date"
						value={editForm?.due_date?.slice(0, 10) || ""}
						onChange={handleEditChange}
						fullWidth
						margin="normal"
						InputLabelProps={{ shrink: true }}
					/>
					<TextField
						label="Status"
						name="status"
						value={editForm?.status || ""}
						onChange={handleEditChange}
						fullWidth
						margin="normal"
					/>
					<Typography variant="subtitle1" sx={{ mt: 2 }}>
						Line Items
					</Typography>
					{editForm?.line_items.map((item, index) => (
						<Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
							<TextField
								label="Description"
								value={item.description}
								onChange={(e) =>
									handleEditLineItemChange(index, "description", e.target.value)
								}
								fullWidth
								size="small"
							/>
							<TextField
								label="Quantity"
								type="number"
								value={item.quantity}
								onChange={(e) =>
									handleEditLineItemChange(
										index,
										"quantity",
										Number(e.target.value),
									)
								}
								size="small"
								sx={{ width: 100 }}
							/>
							<TextField
								label="Price"
								type="number"
								value={item.price}
								onChange={(e) =>
									handleEditLineItemChange(
										index,
										"price",
										Number(e.target.value),
									)
								}
								size="small"
								sx={{ width: 100 }}
							/>
							<Button
								size="small"
								color="error"
								onClick={() => removeEditLineItem(index)}
							>
								Remove
							</Button>
						</Box>
					))}
					<Button size="small" onClick={addEditLineItem} sx={{ mt: 1 }}>
						Add Line Item
					</Button>
					<Typography variant="subtitle1" sx={{ mt: 2 }}>
						Total: ${editForm?.total.toFixed(2)}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleEditClose}>Cancel</Button>
					<Button onClick={handleEditSave} variant="contained" color="primary">
						Save
					</Button>
				</DialogActions>
			</Dialog>

			{/* Mark as Paid Dialog */}
			<Dialog
				open={Boolean(markAsPaidAnchorEl)}
				onClose={handleMarkAsPaidClose}
				fullWidth
				maxWidth="xs"
			>
				<DialogTitle>Mark Invoice as Paid</DialogTitle>
				<DialogContent>
					<TextField
						label="Payment Amount"
						name="paymentAmount"
						type="number"
						value={markAsPaidForm.paymentAmount}
						onChange={handleMarkAsPaidChange}
						fullWidth
						margin="normal"
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">$</InputAdornment>
							),
						}}
					/>
					<TextField
						label="Payment Date"
						name="paymentDate"
						type="date"
						value={markAsPaidForm.paymentDate}
						onChange={handleMarkAsPaidChange}
						fullWidth
						margin="normal"
						InputLabelProps={{ shrink: true }}
					/>

					<TextField
						label="Payment Method"
						name="paymentMethod"
						value={markAsPaidForm.paymentMethod}
						onChange={handleMarkAsPaidChange}
						fullWidth
						margin="normal"
					/>
					<TextField
						label="Payment Notes"
						name="paymentNotes"
						value={markAsPaidForm.paymentNotes}
						onChange={handleMarkAsPaidChange}
						fullWidth
						margin="normal"
						multiline
						rows={3}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleMarkAsPaidClose} color="primary">
						Cancel
					</Button>
					<Button
						onClick={handleMarkAsPaidSubmit}
						color="primary"
						variant="contained"
					>
						Submit
					</Button>
				</DialogActions>
			</Dialog>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={deleteDialogOpen}
				onClose={handleDeleteClose}
				fullWidth
				maxWidth="xs"
			>
				<DialogTitle>Confirm Delete</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to delete invoice {selectedDeleteInvoice?.id}?
						This cannot be undone.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleDeleteClose} color="primary">
						No
					</Button>
					<Button
						onClick={handleDeleteConfirm}
						color="error"
						variant="contained"
					>
						Yes, Delete
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
